import { computed, ref } from 'vue'
import type { PreferredLanguageEnum } from '@apiTypes'
import i18n, { isLanguageSupported, supportedLanguages } from '@/plugins/i18n'
import { useAuthStore } from '@/stores/auth'

const _currentLanguage = ref<string>(localStorage.getItem('user-language') || supportedLanguages[0])
const _initialized = ref<boolean>(false)

export function useLanguageService() {
  const authStore = useAuthStore()

  const currentLanguage = computed<string>(() => _currentLanguage.value)

  function syncI18nLocale(lang: string): void {
    if (i18n.global.locale.value !== lang)
      i18n.global.locale.value = lang
  }

  async function initializeLanguage(): Promise<void> {
    let langToSet: string
    const langFromStorage = localStorage.getItem('user-language')

    if (langFromStorage && isLanguageSupported(langFromStorage))
      langToSet = langFromStorage
    else if (authStore.currentUser?.preferred_language && isLanguageSupported(authStore.currentUser.preferred_language))
      langToSet = authStore.currentUser.preferred_language
    else
      langToSet = 'en'

    if (!_initialized.value || _currentLanguage.value !== langToSet) {
      await setLanguage(langToSet as PreferredLanguageEnum)
    }
    else {
      if (!_initialized.value)
        syncI18nLocale(_currentLanguage.value)
    }

    _initialized.value = true
  }

  async function setLanguage(lang: PreferredLanguageEnum | string): Promise<void> {
    const langStr = lang as string
    if (!isLanguageSupported(langStr))
      throw new Error(`Unsupported language: ${langStr}`)

    syncI18nLocale(langStr)
    _currentLanguage.value = langStr
    localStorage.setItem('user-language', langStr)

    if (authStore.currentUser && authStore.currentUser.preferred_language !== langStr)
      await authStore.updateLanguage(langStr as PreferredLanguageEnum)
  }

  syncI18nLocale(_currentLanguage.value)

  return {
    currentLanguage,
    initializeLanguage,
    setLanguage,
    _initialized,
  }
}
